import {
  Button,
  DateTimePickerWithRange,
  FancyMultiSelect,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectType,
  SelectValue,
  Switch,
  Textarea,
  TimeDurationPicker,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useLocalization } from '@ais-ui/locale-management';
import { useFetchPSSWithType, useGetUserMmsi } from '@ais-ui/api';
import { ScheduleMessageFormValues } from './scheduleMessageForm';
import { useGetSelectables } from '@ais-ui/websockets';
import { ShapeSelect } from '../../Shapes/ShapeSelects/ShapeSelect';
import { DateRange } from 'react-day-picker';
import { PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { GroupMultiSelect } from '../../Groups/GroupMultiSelect';
import { useTimeZoneContext } from '@ais-ui/utils';
import { useDateTimeFormat } from '../../conversions/useDateFormat';

interface ScheduleMessageFormProps {
  form: UseFormReturn<ScheduleMessageFormValues>;
  mutateType: 'edit' | 'create';
}

interface ScheduleFormFieldProps {
  form: UseFormReturn<ScheduleMessageFormValues>;
}

interface DateTimePickerRangeInputProps {
  value: { from: Date | undefined; to: Date | undefined };
  index: number;
  onScheduleChange: (index: number, date: DateRange | undefined) => void;
}

const DateTimePickerRangeInput = ({
  value,
  index,
  onScheduleChange,
}: DateTimePickerRangeInputProps) => {
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  const [date, setDate] = React.useState<DateRange | undefined>(undefined);

  useEffect(() => {
    if (value.from && value.to) {
      setDate({ from: value.from, to: value.to });
    }
  }, [value.from, value.to]);

  useEffect(() => {
    onScheduleChange(index, date);
  }, [date, index, onScheduleChange]);

  if (!date && value.from && value.to) {
    return null; // We have to return null in this case, as date needs to be set before initializing the DateTimePickerWithRange
  }

  return (
    <DateTimePickerWithRange
      className="w-full"
      date={date}
      setDate={setDate}
      timeZone={timeZone}
      dateTimeFormat={userDateFormat}
      dateLimit="future"
    ></DateTimePickerWithRange>
  );
};

const ScheduleFormField = ({ form }: ScheduleFormFieldProps) => {
  const { messages } = useLocalization();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'schedule',
  });

  const onScheduleChange = useCallback(
    (index: number, date: DateRange | undefined) => {
      form.setValue(`schedule.${index}.start`, date?.from);
      form.setValue(`schedule.${index}.end`, date?.to);
      fields[index].start = date?.from;
      fields[index].end = date?.to;
    },
    [form, fields],
  );

  return (
    <FormField
      key="schedule"
      control={form.control}
      name="schedule"
      render={({ field }) => (
        <FormItem>
          <div className="flex justify-between">
            <div className="flex space-x-4 items-center">
              <FormLabel>Schedule</FormLabel>
            </div>
            <InfoIconTooltip>
              Enter the periods where you want the message to be broadcasted
            </InfoIconTooltip>
          </div>

          <FormControl onChange={() => form.clearErrors()}>
            <>
              {fields.map((schedule, index) => (
                <div key={`${index}`} className="flex space-x-2">
                  <Button
                    type="button"
                    size="icon"
                    disabled={true}
                    variant="ghost"
                    className="h-9 w-9 flex-shrink-0"
                    onClick={() => remove(index)}
                  >
                    {index + 1}
                  </Button>
                  <DateTimePickerRangeInput
                    value={{ from: schedule.start, to: schedule.end }}
                    index={index}
                    onScheduleChange={onScheduleChange}
                  ></DateTimePickerRangeInput>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        id="remove-schedule-button"
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-9 w-9 flex-shrink-0 flex-grow-0"
                        onClick={() => remove(index)}
                      >
                        <PiMinusThin className="size-4"></PiMinusThin>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Remove schedule</TooltipContent>
                  </Tooltip>
                </div>
              ))}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    id="add-schedule-button"
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9"
                    onClick={() =>
                      append({
                        start: new Date(),
                        end: new Date(
                          new Date().getTime() + 24 * 60 * 60 * 1000,
                        ),
                      })
                    }
                  >
                    <PiPlusThin className="size-4"></PiPlusThin>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Add schedule</TooltipContent>
              </Tooltip>
            </>
          </FormControl>
          {form.formState.errors.schedule && (
            <p className="text-sm font-medium text-destructive">Error</p>
          )}
        </FormItem>
      )}
    />
  );
};

export const ScheduleMessageForm = ({
  form,
  mutateType,
}: ScheduleMessageFormProps) => {
  const { messages } = useLocalization();

  const { data: fromMmsis } = useGetUserMmsi();
  const { data: pss } = useFetchPSSWithType('aton');
  const { data: geographicNoticeTypes } = useGetSelectables('gn_notice_desc');

  const geographicNoticeSelectTypes = useMemo(() => {
    if (!Array.isArray(geographicNoticeTypes)) {
      return [];
    }

    return geographicNoticeTypes.map(
      (geographicNoticeType) =>
        ({
          label: geographicNoticeType?.name
            ? geographicNoticeType.name.toString()
            : '',
          value: geographicNoticeType?.name
            ? geographicNoticeType.id.toString()
            : '',
        }) as SelectType,
    );
  }, [geographicNoticeTypes]);

  const fromMMSIs: SelectType[] = useMemo(() => {
    if (!fromMmsis || !fromMmsis.records) {
      return [];
    }

    return fromMmsis.records.map(
      (fromMmsi) =>
        ({
          label: fromMmsi?.name ? fromMmsi.name.toString() : '',
          value: fromMmsi?.name ? fromMmsi.name.toString() : '',
        }) as SelectType,
    );
  }, [fromMmsis]);

  const allowedPSS: SelectType[] = useMemo(() => {
    if (!pss || !pss.records) {
      return [];
    }

    const filteredPSS = pss.records.filter(
      (pssItem) => pssItem.allowed === true,
    );

    return filteredPSS.map(
      (pssItem) =>
        ({
          label: pssItem.name,
          value: pssItem.uuid,
        }) as SelectType,
    );
  }, [pss]);

  return (
    <div className="space-y-8">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem className="flex flex-col space-y-4">
            <div className="flex justify-between">
              <FormLabel>Name *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                id="schedule-message-name-field"
                type="text"
                placeholder="Enter message schedule name"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="enabled"
        render={({ field }) => (
          <FormItem className="flex flex-col space-y-4">
            <div className="flex justify-between">
              <FormLabel>Enable</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Switch
                checked={field.value}
                onCheckedChange={(value) => {
                  field.onChange(value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="messageType"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Type *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                disabled={mutateType === 'edit'}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
                value={field.value}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose message type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="safety" value="safety">
                    Safety related
                  </SelectItem>
                  <SelectItem key="geographicnotice" value="geographicnotice">
                    Geographic notice
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="sender"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Sender MMSI *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                defaultValue=""
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    id="schedule-message-pss-select"
                    defaultValue=""
                    placeholder="Select MMSI to send from"
                  />
                </SelectTrigger>
                <SelectContent>
                  {fromMMSIs.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      id={
                        form.watch('messageType') === 'safety' ||
                        form.watch('messageType') === 'geographicnotice'
                          ? `sender-mmsi-${option.value}-button`
                          : undefined
                      }
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {form.getValues('messageType') === 'geographicnotice' && (
        <FormField
          control={form.control}
          name="action"
          render={({ field }) => {
            return (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>Action *</FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="advisement" value="advisement">
                        Advisement
                      </SelectItem>
                      <SelectItem key="directive" value="directive">
                        Directive
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}
      {form.getValues('messageType') === 'geographicnotice' && (
        <FormField
          control={form.control}
          name="shapeId"
          render={({ field }) => {
            return (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>Shape *</FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <ShapeSelect
                    value={field.value}
                    onChange={field.onChange}
                  ></ShapeSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}
      {form.getValues('messageType') === 'geographicnotice' && (
        <FormField
          control={form.control}
          name="noticeType"
          render={({ field }) => {
            return (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>Notice type *</FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose notice type" />
                    </SelectTrigger>
                    <SelectContent>
                      {geographicNoticeSelectTypes.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}
      <FormField
        control={form.control}
        name="message"
        render={({ field }) => {
          return (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>Message *</FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Textarea
                  placeholder={messages.sendMessageFormMessagePlaceholder}
                  id="send-message-message"
                  {...field}
                  maxLength={85}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="pss"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>PSS *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <FancyMultiSelect
                value={field.value}
                onChange={field.onChange}
                availableSelectables={allowedPSS}
                placeholder="Choose at least one PSS"
              ></FancyMultiSelect>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        key="broadcastInterval"
        control={form.control}
        name="broadcastInterval"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Broadcast interval *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <TimeDurationPicker
                value={field.value}
                onChange={field.onChange}
                format="HH:MM:SS"
              ></TimeDurationPicker>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <ScheduleFormField form={form}></ScheduleFormField>
      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Share with</FormLabel>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
